import { useCallback, useEffect, useRef, useState } from "react";
import { ChatApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { ChatMessage, Stage } from "../lusca-search-api/generated-src";

export interface ChatEntry {
  id: string;
  role: "user" | "assistant";
  content: string;
  isError?: boolean;
  // Steps the assistant ran to produce this answer, kept for the collapsed summary.
  steps?: string[];
}

let entryCounter = 0;
const nextId = (): string => {
  entryCounter += 1;
  return `chat-entry-${entryCounter}`;
};

// Poll cadence: fast enough that steps feel live, slow enough to stay cheap on a 1-2 minute turn.
const POLL_INTERVAL_MS = 1200;
// Backstop so a worker that dies without writing a terminal status can't spin forever.
const MAX_POLL_MS = 300_000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// History is client-side only: every request re-sends the full
// conversation, so the backend stays stateless.
export default function useChat(stage: Stage, transactionId: string) {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  // AI-suggested follow-ups for the latest answer. Fetched on a separate
  // request AFTER the answer renders, so it never delays the answer.
  const [aiFollowups, setAiFollowups] = useState<string[]>([]);
  // True while the follow-up call is in flight, so the UI can show a loading
  // state instead of flashing generic chips that then swap to the AI ones.
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Steps completed by the in-flight turn, rendered live in place of a spinner.
  const [progress, setProgress] = useState<string[]>([]);

  // Guards against stale follow-up responses: each call takes a request id, and
  // only the latest one is allowed to apply state. A new sendMessage bumps the
  // id, so an in-flight response from a previous question is ignored.
  const followupRequestId = useRef(0);
  // Same guard for the turn itself: a reset or a newer question invalidates an
  // in-flight poll loop, so its late result can never append to the new conversation.
  const turnId = useRef(0);
  // Set on unmount so a poll loop stops instead of calling setState on a dead component.
  const unmounted = useRef(false);

  useEffect(
    () => () => {
      unmounted.current = true;
    },
    []
  );

  // Best-effort fast Haiku call for follow-ups; failures leave the list empty.
  const fetchFollowups = useCallback(
    (history: ChatMessage[]) => {
      const api = ChatApiFactory();
      const requestId = ++followupRequestId.current;
      const isLatest = () => requestId === followupRequestId.current;
      setFollowupsLoading(true);
      api
        .chatFollowups(stage, { transactionId, messages: history })
        .then(({ data }) => {
          if (isLatest()) setAiFollowups(data.followups ?? []);
        })
        .catch(() => {
          if (isLatest()) setAiFollowups([]);
        })
        .finally(() => {
          if (isLatest()) setFollowupsLoading(false);
        });
    },
    [stage, transactionId]
  );

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      const nextEntries: ChatEntry[] = [
        ...entries.filter((e) => !e.isError),
        { id: nextId(), role: "user", content: trimmed },
      ];
      setEntries(nextEntries);
      // Clear the previous answer's follow-ups while the new one is pending, and
      // invalidate any in-flight follow-up call so its stale response is ignored.
      followupRequestId.current += 1;
      setAiFollowups([]);
      setFollowupsLoading(false);
      setProgress([]);
      setIsLoading(true);

      const history = nextEntries.map(({ role, content }) => ({
        role,
        content,
      }));
      const requestId = ++turnId.current;
      const isCurrent = () =>
        requestId === turnId.current && !unmounted.current;

      const appendAssistant = (
        content: string,
        steps: string[],
        isError?: boolean
      ) => {
        setEntries((prev) => [
          ...prev,
          { id: nextId(), role: "assistant", content, steps, isError },
        ]);
      };

      // Start the turn, then poll its job until it reports an answer or a failure.
      const run = async () => {
        const api = ChatApiFactory();
        const { data: started } = await api.chatAsync(stage, {
          transactionId,
          messages: history,
        });
        const jobId = started.jobId;
        if (!jobId) throw new Error("no job id returned");

        const deadline = Date.now() + MAX_POLL_MS;
        let steps: string[] = [];
        while (Date.now() < deadline) {
          await sleep(POLL_INTERVAL_MS);
          if (!isCurrent()) return;
          // A failed poll does not mean the worker died, so retry until the deadline.
          const polled = await api.chatStatus(stage, jobId).catch(() => null);
          if (!polled) continue;
          const { data } = polled;
          steps = data.progress ?? steps;
          if (!isCurrent()) return;
          setProgress(steps);
          if (data.status === "FAILED") {
            throw new Error(data.error ?? "Chat failed. Please retry.");
          }
          if (data.status === "DONE") {
            const answer = data.content ?? "(no response)";
            appendAssistant(answer, steps);
            setIsLoading(false);
            setProgress([]);
            // After the answer renders, fetch tailored follow-ups (separate call).
            fetchFollowups([
              ...history,
              { role: "assistant", content: answer },
            ]);
            return;
          }
        }
        throw new Error("the assistant took too long to respond");
      };

      run().catch((error: unknown) => {
        if (!isCurrent()) return;
        appendAssistant(
          `Something went wrong: ${
            error instanceof Error ? error.message : "unknown error"
          }. Please try again.`,
          [],
          true
        );
        setIsLoading(false);
        setProgress([]);
      });
    },
    [entries, isLoading, stage, transactionId, fetchFollowups]
  );

  const reset = useCallback(() => {
    // Invalidate any in-flight turn so its result cannot land in the new conversation.
    turnId.current += 1;
    setEntries([]);
    setAiFollowups([]);
    setFollowupsLoading(false);
    setProgress([]);
    setIsLoading(false);
  }, []);

  return {
    entries,
    aiFollowups,
    followupsLoading,
    progress,
    sendMessage,
    reset,
    isLoading,
  };
}
